// src/components/AthleteCalendar/AthleteCalendar.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Swal from 'sweetalert2';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import axios from 'axios';
import { dbTypeToIcon } from '../../utils/activityMapping';
import '../styles/AthleteCalendar.css';
import { useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';

const API_URL = `${API_BASE_URL.replace(/\/+$/, '')}/api`;



// 🔊 Logs (deja false para no colapsar la consola en dev)
const DBG = false;
const log = (...args) => { if (DBG) console.log('[CAL]', ...args); };
const pad2 = (n) => String(n).padStart(2, '0');

/* ================= PASSIVE EVENTS PATCH ================= */
function installPassiveEventsPatch() {
  if (typeof window === 'undefined' || window.__passivePatched) return;
  const supportsPassive = (() => {
    let ok = false;
    try {
      const opts = Object.defineProperty({}, 'passive', {
        get() { ok = true; return true; }
      });
      window.addEventListener('test', null, opts);
      window.removeEventListener('test', null, opts);
    } catch {}
    return ok;
  })();
  if (!supportsPassive) { window.__passivePatched = true; return; }

  const origAdd = EventTarget.prototype.addEventListener;
  const origRemove = EventTarget.prototype.removeEventListener;
  const passiveTypes = new Set(['touchstart', 'touchmove', 'wheel']);

  EventTarget.prototype.addEventListener = function(type, listener, options) {
    if (passiveTypes.has(type)) {
      if (options === undefined) {
        options = { passive: true };
      } else if (typeof options === 'boolean') {
        options = { capture: options, passive: true };
      } else if (typeof options === 'object' && !('passive' in options)) {
        options = { ...options, passive: true };
      }
    }
    return origAdd.call(this, type, listener, options);
  };

  EventTarget.prototype.removeEventListener = function(type, listener, options) {
    if (typeof options === 'boolean') options = { capture: options };
    return origRemove.call(this, type, listener, options);
  };

  window.__passivePatched = true;
}

/* ================= helpers fecha/tiempo ================= */

const atLocalNoon = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  return new Date(x.getFullYear(), x.getMonth(), x.getDate(), 12, 0, 0, 0);
};

// 🔸 Siempre medianoche local → ISO (UTC)
const toLocalMidnightIso = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  const localMidnight = new Date(
    x.getFullYear(),
    x.getMonth(),
    x.getDate(),
    12, 0, 0, 0
  );
  return localMidnight.toISOString();
};

const normalizeEventForCalendar = (ev) => {
  const rawStart = ev?.date || ev?.start;
  if (!rawStart) return ev;

  const d = new Date(rawStart);

  // 👉 start y end el MISMO día (para que FullCalendar no lo trate como allDay raro)
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0);
  const end = new Date(start.getTime() + 60 * 1000); // +1 minuto

  return {
    ...ev,
    start,
    end,
    allDay: false,
  };
};

const durationIsoToMinutes = (isoStr) => {
  if (!isoStr || typeof isoStr !== 'string') return 0;
  const [hh = '0', mm = '0'] = isoStr.split(':');
  const h = parseInt(hh, 10) || 0;
  const m = parseInt(mm, 10) || 0;
  return h * 60 + m;
};

const minutesToIso = (mins) => {
  const m = Math.max(0, parseInt(mins, 10) || 0);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${pad2(h)}:${pad2(mm)}:00`;
};

const isZeroIso = (s) => {
  const v = String(s ?? '').trim();
  return v === '00:00:00' || v === '0:00:00' || v === '00:00' || v === '0:00';
};

// === helpers planned/normalización ===
const sanitizeDistance = (raw) => {
  const s = String(raw ?? '').toLowerCase().replace(',', '.').replace(/k(m)?\b/g, '').trim();
  const v = parseFloat(s);
  return Number.isFinite(v) && v > 0 ? Number(v) : null;
};

const parseDurationToIso = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase().replace(',', '.');
  if (!s) return null;

  if (s.includes(':')) {
    const parts = s.split(':').map((x) => x.trim());
    if (parts.length === 2) {
      const [h = '0', m = '0'] = parts;
      const H = Math.max(0, parseInt(h, 10) || 0);
      const M = Math.max(0, parseInt(m, 10) || 0);
      if (H === 0 && M === 0) return null;
      return `${pad2(H)}:${pad2(M)}:00`;
    }
    if (parts.length >= 3) {
      const [h = '0', m = '0', ss = '0'] = parts;
      const H = Math.max(0, parseInt(h, 10) || 0);
      const M = Math.max(0, parseInt(m, 10) || 0);
      const S = Math.max(0, parseInt(ss, 10) || 0);
      if (H === 0 && M === 0 && S === 0) return null;
      return `${pad2(H)}:${pad2(M)}:${pad2(S)}`;
    }
  }

  if (/^\d+(\.\d+)?\s*h/.test(s)) {
    const hours = parseFloat(s) || 0;
    const mins = Math.round(hours * 60);
    if (mins <= 0) return null;
    return minutesToIso(mins);
  }

  const mins = parseFloat(s);
  if (Number.isFinite(mins) && mins > 0) return minutesToIso(Math.round(mins));
  return null;
};

const normalizeType = (raw) => {
  const t = String(raw || '').toLowerCase().trim();
  if (t.startsWith('nad') || t.includes('swim')) return 'nadar';
  if (t.startsWith('cicl') || t.includes('bike') || t.includes('cycl')) return 'ciclismo';
  if (t.startsWith('fuer') || t.includes('strength') || t.includes('gym')) return 'fuerza';
  if (t.startsWith('cam') || t.includes('walk')) return 'caminar';
  if (t.includes('desc') || t.includes('rest')) return 'día de descanso';
  if (t.startsWith('cor') || t.includes('run')) return 'correr';
  return 'otro';
};

const iconKeyFromType = (val) =>
  normalizeType(val) === 'día de descanso' ? 'descanso' : normalizeType(val);

const labelFromType = (raw, t) => {
  const type = normalizeType(raw);
  switch (type) {
    case 'nadar': return t('activityLabels.swimming');
    case 'ciclismo': return t('activityLabels.cycling');
    case 'fuerza': return t('activityLabels.strength');
    case 'caminar': return t('activityLabels.walking');
    case 'día de descanso': return t('activityLabels.rest');
    case 'correr': return t('activityLabels.running');
    default: return t('activityLabels.training');
  }
};

const safeTitle = (title, type, t) => {
  const s = String(title ?? '').trim().toLowerCase();
  // Si vacío o "sin título"
  if (!s || s === t('activityLabels.untitled') || s === t('activityLabels.untitledActivity')) {
    return labelFromType(type, t);
  }
  // Si el título ES el nombre del deporte guardado en DB (en español) → traducir
  const normalized = normalizeType(s);
  if (normalized !== 'otro') {
    return labelFromType(normalized, t);
  }
  return String(title ?? '').trim();
};

async function ensureSport(typeCandidate, t) {
  let type = normalizeType(typeCandidate);
  if (type !== 'otro') return type;

  const { value, isConfirmed } = await Swal.fire({
    title: t('calendar.selectSport'),
    input: 'select',
    inputOptions: {
      correr: t('activityLabels.running'),
      ciclismo: t('activityLabels.cycling'),
      nadar: t('activityLabels.swimming'),
      fuerza: t('activityLabels.strength'),
      caminar: t('activityLabels.walking'),
      'día de descanso': t('activityLabels.rest'),
      otro: t('activityLabels.other')
    },
    inputValue: 'correr',
    showCancelButton: true,
    confirmButtonText: t('common.accept'),
    cancelButtonText: t('common.cancel')
  });
  if (!isConfirmed) throw new Error('cancelado');
  return value || 'correr';
}

function extractPlannedFromDrag(draggedEl, tplPlanned = {}, xpPlanned = {}) {
  const ds = draggedEl?.dataset || {};
  const attrDurationIso = draggedEl?.getAttribute?.('data-duration') ?? ds.duration ?? ds.durationIso ?? null;
  const attrDuracionMin = draggedEl?.getAttribute?.('data-duracion') ?? ds.duracion ?? null;
  const attrDistanceKm = draggedEl?.getAttribute?.('data-distance') ?? draggedEl?.getAttribute?.('data-distancia') ?? ds.distance ?? ds.distancia ?? null;
  const attrMetric = draggedEl?.getAttribute?.('data-metric') ?? ds.metric ?? null;
  const attrValue = draggedEl?.getAttribute?.('data-value') ?? ds.value ?? null;

  const planned = { ...(tplPlanned || {}), ...(xpPlanned || {}) };

  if (attrMetric && attrValue != null) {
    const m = String(attrMetric).toLowerCase();
    if (m.includes('dist')) planned.distancia = sanitizeDistance(attrValue);
    if (m.includes('dur')) {
      if (String(attrValue).includes(':')) planned.durationIso = String(attrValue);
      else planned.duracion = parseInt(attrValue, 10);
    }
  }
  if (attrDistanceKm != null && planned.distancia == null) {
    planned.distancia = sanitizeDistance(attrDistanceKm);
  }
  if (attrDurationIso && !planned.durationIso) planned.durationIso = String(attrDurationIso);
  if (attrDuracionMin != null && planned.duracion == null) {
    const n = parseInt(attrDuracionMin, 10);
    if (!Number.isNaN(n)) planned.duracion = n;
  }

  if (planned.durationIso && planned.duracion == null) planned.duracion = durationIsoToMinutes(planned.durationIso);
  if (planned.duracion != null && !planned.durationIso) planned.durationIso = minutesToIso(planned.duracion);

  let plannedMetric = null;
  const out = {};
  if (planned.distancia != null && !Number.isNaN(planned.distancia) && planned.distancia > 0) {
    out.distancia = Number(planned.distancia);
    plannedMetric = 'distancia';
  } else if (planned.duracion != null && !Number.isNaN(planned.duracion) && planned.duracion > 0) {
    out.duracion = Number(planned.duracion);
    out.durationIso = planned.durationIso;
    plannedMetric = 'duracion';
  }

  log('🧩 extractPlannedFromDrag OUT →', { planned: out, plannedMetric });
  return { planned: out, plannedMetric };
}

async function ensurePlannedOrAsk(basePlanned, type, t) {
  if (basePlanned?.distancia && basePlanned.distancia > 0) {
    return { planned: { distancia: Number(basePlanned.distancia) }, plannedMetric: 'distancia' };
  }
  if (basePlanned?.durationIso && !isZeroIso(basePlanned.durationIso)) {
    const mins = durationIsoToMinutes(basePlanned.durationIso);
    if (mins > 0) {
      return {
        planned: { duracion: mins, durationIso: basePlanned.durationIso },
        plannedMetric: 'duracion'
      };
    }
  }
  if (basePlanned?.duracion && basePlanned.duracion > 0) {
    return {
      planned: { duracion: Number(basePlanned.duracion), durationIso: minutesToIso(basePlanned.duracion) },
      plannedMetric: 'duracion'
    };
  }

  const step1 = await Swal.fire({
    title: t('calendar.defineMetric'),
    text: t('calendar.planByMetric', { sport: labelFromType(type, t) }),
    input: 'select',
    inputOptions: {
      distancia: t('calendar.distanceKm'),
      duracion: t('calendar.duration')
    },
    inputValue: 'duracion',
    showCancelButton: true,
    confirmButtonText: t('calendar.next'),
    cancelButtonText: t('common.cancel')
  });
  if (!step1.isConfirmed) throw new Error('cancelado');

  const metric = step1.value;

  if (metric === 'distancia') {
    const step2 = await Swal.fire({
      title: t('calendar.distanceKm'),
      input: 'text',
      inputLabel: t('calendar.enterKmExample'),
      inputPlaceholder: t('calendar.enterKmPlaceholder'),
      inputValidator: (val) => {
        const v = sanitizeDistance(val);
        if (!v) return t('calendar.enterDistanceGreaterThanZero');
        return undefined;
      },
      showCancelButton: true,
      confirmButtonText: t('common.accept'),
      cancelButtonText: t('common.cancel')
    });
    if (!step2.isConfirmed) throw new Error('cancelado');
    const v = sanitizeDistance(step2.value);
    return { planned: { distancia: v }, plannedMetric: 'distancia' };
  }

  const step2 = await Swal.fire({
    title: t('calendar.duration'),
    input: 'text',
    inputLabel: t('calendar.enterDurationExample'),
    inputPlaceholder: t('calendar.enterDurationPlaceholder'),
    inputValidator: (val) => {
      const iso = parseDurationToIso(val);
      if (!iso || isZeroIso(iso)) return t('calendar.enterDurationGreaterThanZero');
      return undefined;
    },
    showCancelButton: true,
    confirmButtonText: t('common.accept'),
    cancelButtonText: t('common.cancel')
  });
  if (!step2.isConfirmed) throw new Error('cancelado');
  const isoDur = parseDurationToIso(step2.value);
  return {
    planned: { duracion: durationIsoToMinutes(isoDur), durationIso: isoDur },
    plannedMetric: 'duracion'
  };
}

/* ---------------- API helpers ---------------- */
async function postAdd(payload) {
  const { data } = await axios.post(`${API_URL}/activities/add`, payload, { withCredentials: true });
  return data;
}

async function delDelete(id) {
  const { data } = await axios.delete(`${API_URL}/activities/delete/${id}`, { withCredentials: true });
  return data;
}

// Actualiza sólo la fecha usando la ruta /update/:id del backend
async function putMoveDate(id, isoDate) {
  console.log('[putMoveDate] id:', id, 'isoDate:', isoDate);
  const payload = { date: isoDate };
  const { data } = await axios.put(`${API_URL}/activities/update/${id}`, payload, { withCredentials: true });
  return data;
}

/* ======================= COMPONENTE ======================= */
const AthleteCalendar = ({
  events,
  handleDateSelect,
  handleEventClick,
  handleEventDrop,
  fetchEvents,
  renderEventContent: customRenderEventContent,
  isTrainerView = false
}) => {
  const { t } = useTranslation();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [clipboardEvent, setClipboardEvent] = useState(null);
  const calendarRef = useRef(null);

  // control de refetch / rango visible y guardas
  const suppressFetchRef = useRef(false);
  const lastRangeRef = useRef({ start: null, end: null });
  const datesSetTimerRef = useRef(null);
  const lastClickRef = useRef({ id: null, t: 0 });

  // 🔧 Instalar patch passive una sola vez
  useEffect(() => {
    installPassiveEventsPatch();
  }, []);

  /* ---------- INIT external Draggable (paleta) ---------- */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const containers = [
      document.querySelector('#external-events'),
      document.querySelector('#planillas-panel'),
      document.querySelector('[data-fc-draggable="true"]'),
    ].filter(Boolean);

    const ensureDraggableItems = (cont) => {
      const candidates = cont.querySelectorAll('[data-template], [data-type], .draggable-item, .fc-draggable');
      candidates.forEach((el) => {
        el.classList.add('fc-draggable');
        el.setAttribute('draggable', 'true');
        el.setAttribute('data-fc-item', 'true');
      });
      return candidates.length;
    };

    containers.forEach((cont) => {
      const count = ensureDraggableItems(cont);
      if (cont.__fcDraggable) {
        log('♻️ Draggable ya existía', cont, 'items:', count);
        return;
      }

      cont.__fcDraggable = new Draggable(cont, {
        itemSelector: '[data-fc-item], .draggable-item, .fc-draggable, [data-type], [data-template]',
        eventData: (el) => {
          let tpl = null;
          try { tpl = JSON.parse(el.getAttribute('data-template') || 'null'); } catch { tpl = null; }

          const rawType =
            (tpl && tpl.type) ||
            el.getAttribute('data-type') ||
            el.getAttribute('data-label') ||
            el.getAttribute('data-title') ||
            'otro';

          const type = normalizeType(rawType);
          const title =
            el.getAttribute('data-title') ||
            el.getAttribute('data-label') ||
            (tpl && tpl.title) ||
            labelFromType(type, t);

          const ds = el.dataset || {};
          const metricAttr = el.getAttribute('data-metric') ?? ds.metric ?? null;
          const valueAttr = el.getAttribute('data-value') ?? ds.value ?? null;
          const attrDurationIso = el.getAttribute('data-duration') ?? ds.duration ?? null;
          const attrDuracionMin = el.getAttribute('data-duracion') ?? ds.duracion ?? null;
          const attrDistanceKm = el.getAttribute('data-distance') ?? el.getAttribute('data-distancia') ?? ds.distance ?? ds.distancia ?? null;

          const planned = { ...(tpl?.planned || {}) };

          if (metricAttr && valueAttr != null) {
            const m = String(metricAttr).toLowerCase();
            if (m.includes('dist')) planned.distancia = sanitizeDistance(valueAttr);
            if (m.includes('dur')) {
              if (String(valueAttr).includes(':')) planned.durationIso = String(valueAttr);
              else planned.duracion = parseInt(valueAttr, 10);
            }
          }

          if (attrDistanceKm != null && planned.distancia == null) {
            planned.distancia = sanitizeDistance(attrDistanceKm);
          }

          if (attrDurationIso && !planned.durationIso) planned.durationIso = String(attrDurationIso);

          if (attrDuracionMin != null && planned.duracion == null) {
            const n = parseInt(attrDuracionMin, 10);
            if (!Number.isNaN(n)) planned.duracion = n;
          }

          if (planned.durationIso && planned.duracion == null) planned.duracion = durationIsoToMinutes(planned.durationIso);
          if (planned.duracion != null && !planned.durationIso) planned.durationIso = minutesToIso(planned.duracion);

          log('🎯 [eventData] start drag →', { title, type, dataset: ds, tplPlanned: tpl?.planned, planned });

          return {
            title,
            extendedProps: {
              type,
              description: (tpl && tpl.description) || '',
              planned,
              templateId: (tpl && (tpl._id || tpl.id)) || null,
            },
          };
        },
      });

      log('🧲 Draggable inicializado', cont, '| items detectados:', count);
    });
  }, [t]);

  /* ---------- BORRAR: garantiza borrado real + limpieza UI ---------- */
  const handleDelete = useCallback(async () => {
    const id = selectedEvent?.id;
    if (!id) return;

    const res = await Swal.fire({
      title: t('common.areYouSure'),
      text: t('calendar.deleteActivityText', { title: selectedEvent.title }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('calendar.yesDelete'),
      cancelButtonText: t('common.cancel'),
      confirmButtonColor: '#FF5252',
      cancelButtonColor: '#aaa'
    });
    if (!res.isConfirmed) return;

    const prevSelectedId = String(id);

    try {
      suppressFetchRef.current = true;

      log('🗑️ DELETE →', id);
      const out = await delDelete(id);

      if (out && out.ok === false) {
        throw new Error(out.message || t('calendar.couldNotDeleteActivity'));
      }

      const api = calendarRef.current?.getApi?.();
      const ev = api?.getEventById?.(id);
      if (ev) ev.remove();

      setSelectedEvent((prev) => {
        if (!prev) return prev;
        return String(prev.id) === prevSelectedId ? null : prev;
      });

      await fetchEvents?.(lastRangeRef.current.start, lastRangeRef.current.end);

      Swal.fire({
        title: t('common.deleted'),
        icon: 'success',
        timer: 1200,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('❌ Error al borrar actividad:', error);
      const msg = error?.response?.data?.message || error?.message || t('calendar.couldNotDeleteActivity');
      Swal.fire(t('calendar.errorTitle'), msg, 'error');
    } finally {
      suppressFetchRef.current = false;
    }
  }, [selectedEvent, fetchEvents, t]);

  const handleCopy = useCallback(() => {
    if (!selectedEvent) return;
    const data = selectedEvent.extendedProps || {};

    const copiedEvent = {
      title: selectedEvent.title || t('activityLabels.untitledActivity'),
      description: data.description || t('common.noDescription'),
      type: normalizeType(data.type || 'fuerza'),
      trainer: data.trainer ?? localStorage.getItem('userId') ?? null,
      assignedTo: data.assignedTo ?? localStorage.getItem('userId') ?? null,
      planned: { ...data.planned },
      completed: { ...data.completed },
      icon: data.icon || '/static/icons/strength.png',
      draggable: data.draggable ?? true,
      external: data.external ?? false,
      start: null,
      end: null,
      date: selectedEvent.start || new Date()
    };

    setClipboardEvent(copiedEvent);
    log('📋 COPY →', copiedEvent);

    Swal.fire({
      icon: 'info',
      title: t('calendar.copied'),
      text: t('calendar.copiedText', { title: copiedEvent.title }),
      timer: 1200,
      showConfirmButton: false
    });
  }, [selectedEvent, t]);

  const handlePaste = useCallback(async (selectInfo) => {
    if (!clipboardEvent) return;

    const res = await Swal.fire({
      title: t('calendar.pasteActivityTitle'),
      text: t('calendar.pasteActivityText', { title: clipboardEvent.title }),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: t('calendar.yesPaste'),
      cancelButtonText: t('common.cancel')
    });
    if (!res.isConfirmed) return;

    try {
      const fechaIso = toLocalMidnightIso(selectInfo.start || new Date());
      const rawType = clipboardEvent.type || 'otro';
      const type = await ensureSport(rawType, t);
      const title = safeTitle(clipboardEvent.title, type, t);

      const base = {};
      const dist0 = sanitizeDistance(clipboardEvent?.planned?.distancia ?? clipboardEvent?.planned?.distance);
      const durRaw = clipboardEvent?.planned?.duracion ?? clipboardEvent?.planned?.duration ?? null;
      const durIso0 = typeof durRaw === 'string' && durRaw.includes(':')
        ? durRaw
        : (Number.isFinite(parseInt(durRaw, 10)) ? minutesToIso(parseInt(durRaw, 10)) : null);

      if (dist0 != null) base.distancia = dist0;
      if (durIso0 && !isZeroIso(durIso0)) {
        base.duracion = durationIsoToMinutes(durIso0);
        base.durationIso = durIso0;
      }

      const { planned, plannedMetric } = await ensurePlannedOrAsk(base, type, t);

      const assignedTo = clipboardEvent.assignedTo ?? localStorage.getItem('selectedAthleteId') ?? localStorage.getItem('userId');
      const trainer = isTrainerView ? (localStorage.getItem('userId') || null) : null;

      if (!assignedTo) {
        Swal.fire(t('calendar.errorTitle'), t('calendar.missingAssignedAthlete'), 'error');
        return;
      }

      const payload = {
        title,
        description: clipboardEvent.description || '',
        type,
        assignedTo,
        trainer,
        date: fechaIso,
        planned,
        plannedMetric,
        completed: {},
        metrics: {},
        templateId: clipboardEvent.templateId || null,
      };

      log('📌 PASTE payload →', payload);
      await postAdd(payload);

      setClipboardEvent(null);
      await fetchEvents?.(lastRangeRef.current.start, lastRangeRef.current.end);

      Swal.fire({
        icon: 'success',
        title: t('calendar.pasted'),
        timer: 1200,
        showConfirmButton: false
      });
    } catch (error) {
      if (error?.message === 'cancelado') return;
      const msg = error?.response?.data?.message || error.message || t('calendar.unknownError');
      console.error('❌ Error al pegar actividad:', error);
      Swal.fire(t('calendar.errorTitle'), msg, 'error');
    }
  }, [clipboardEvent, fetchEvents, isTrainerView, t]);

  /* ---------- menú contextual por evento ---------- */
  const openContextMenuForEvent = useCallback((fcEventOrObj) => {
    const hasClipboard = !!clipboardEvent;

    Swal.fire({
      html: `
        <p style="font-size:16px;font-weight:500;margin-bottom:15px;">${t('calendar.selectRequiredOption')}</p>
        <div style="display:flex;justify-content:center;gap:10px;">
          ${
            hasClipboard
              ? `<button id="paste-btn" class="swal2-styled" style="background-color:#2196F3;color:white;">${t('calendar.paste')}</button>`
              : `<button id="copy-btn" class="swal2-styled" style="background-color:#4CAF50;color:white;">${t('calendar.copy')}</button>
                 <button id="delete-btn" class="swal2-styled" style="background-color:#FF5252;color:white;">${t('calendar.delete')}</button>`
          }
        </div>
      `,
      showConfirmButton: false,
      backdrop: true,
      allowOutsideClick: true,
      didOpen: () => {
        if (hasClipboard) {
          document.getElementById('paste-btn')?.addEventListener('click', () => {
            Swal.close();
            const start = fcEventOrObj?.start || atLocalNoon(new Date(fcEventOrObj?.date || Date.now()));
            handlePaste({ start });
          });
        } else {
          document.getElementById('copy-btn')?.addEventListener('click', () => {
            Swal.close();
            setSelectedEvent(fcEventOrObj);
            handleCopy();
          });
          document.getElementById('delete-btn')?.addEventListener('click', () => {
            Swal.close();
            setSelectedEvent(fcEventOrObj);
            handleDelete();
          });
        }
      }
    });
  }, [clipboardEvent, handleCopy, handleDelete, handlePaste, t]);

  const onEventDidMount = useCallback((mountInfo) => {
    const el = mountInfo.el;
    const fcEvent = mountInfo.event;
    el.style.cursor = 'grab';

    if (!el.dataset.ctxBound) {
      el.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        setSelectedEvent(fcEvent);
        openContextMenuForEvent(fcEvent);
      });
      el.dataset.ctxBound = '1';
    }
  }, [openContextMenuForEvent]);

  /* ---------- menú contextual GLOBAL ---------- */
  useEffect(() => {
    const handler = (e) => {
      const isExternalDraggable =
        e.target.closest('[data-fc-item="true"]') ||
        e.target.closest('.fc-draggable') ||
        e.target.closest('[data-type]') ||
        e.target.closest('[data-template]');

      if (isExternalDraggable) return;

      const eventRoot = e.target.closest('.fc-event');
      if (eventRoot) {
        e.preventDefault();

        const customId =
          eventRoot.getAttribute('data-event-id') ||
          eventRoot.querySelector('[data-event-id]')?.getAttribute('data-event-id');

        let evObj = null;
        if (customId) {
          const api = calendarRef.current?.getApi?.();
          const apiEvent = api?.getEventById?.(customId);
          evObj = apiEvent || (events || []).find((ev) => String(ev.id) === String(customId));
        }

        const startGuess = evObj?.start || new Date();
        setSelectedEvent(evObj);
        openContextMenuForEvent(evObj || { start: startGuess });
        return;
      }

      const dayCell = e.target.closest('.fc-daygrid-day');
      if (dayCell && clipboardEvent) {
        e.preventDefault();
        const dateStr = dayCell.getAttribute('data-date');

        Swal.fire({
          html: `
            <p style="font-size:16px;font-weight:500;margin-bottom:15px;">${t('calendar.pasteCopiedOnDate', { date: dateStr })}</p>
            <div style="display:flex;justify-content:center;gap:10px;">
              <button id="paste-only" class="swal2-styled" style="background-color:#2196F3;color:white;">${t('calendar.paste')}</button>
              <button id="cancel-ctx" class="swal2-styled" style="background-color:#aaa;color:white;">${t('common.cancel')}</button>
            </div>
          `,
          showConfirmButton: false,
          backdrop: true,
          allowOutsideClick: true,
          didOpen: () => {
            document.getElementById('paste-only')?.addEventListener('click', () => {
              Swal.close();
              handlePaste({ start: atLocalNoon(new Date(`${dateStr}T12:00:00`)) });
            });
            document.getElementById('cancel-ctx')?.addEventListener('click', () => Swal.close());
          }
        });
      }
    };

    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, [clipboardEvent, events, handlePaste, openContextMenuForEvent, t]);

  /* ---------- SOLO eventReceive para alta por drag externo ---------- */
  const onEventReceive = useCallback(async (info) => {
    try {
      suppressFetchRef.current = true;

      const startIso = toLocalMidnightIso(info?.event?.start || new Date());
      const xp = info?.event?.extendedProps || {};
      const draggedEl = info?.draggedEl;

      log('⬇️ eventReceive →', { start: startIso, title: info?.event?.title, xp, ds: draggedEl?.dataset });

      const rawType =
        draggedEl?.getAttribute?.('data-type') ||
        xp.type ||
        info?.event?.title ||
        'otro';

      const type = await ensureSport(rawType, t);

      const incomingTitle =
        draggedEl?.getAttribute?.('data-title') ||
        draggedEl?.getAttribute?.('data-label') ||
        info?.event?.title ||
        '';

      const title = safeTitle(incomingTitle, type, t);

      const assignedTo = xp.assignedTo || localStorage.getItem('selectedAthleteId') || localStorage.getItem('userId');
      const trainer = isTrainerView ? (localStorage.getItem('userId') || null) : null;

      if (!assignedTo) {
        Swal.fire(t('calendar.errorTitle'), t('calendar.missingAssignedAthlete'), 'error');
        info?.event?.remove?.();
        return;
      }

      let tpl = null;
      try { tpl = JSON.parse(draggedEl?.getAttribute?.('data-template') || 'null'); } catch { tpl = null; }
      const tplPlanned = tpl?.planned || {};

      const { planned: pIn } = extractPlannedFromDrag(draggedEl, tplPlanned, xp.planned);
      const base = { ...pIn };
      if (base.durationIso && isZeroIso(base.durationIso)) {
        delete base.duracion;
        delete base.durationIso;
      }

      const { planned, plannedMetric } = await ensurePlannedOrAsk(base, type, t);

      const payload = {
        title,
        description: xp.description || '',
        type,
        assignedTo,
        trainer,
        date: startIso,
        planned,
        plannedMetric,
        completed: {},
        metrics: {},
        templateId: xp.templateId || tpl?._id || tpl?.id || null,
      };

      await postAdd(payload);

      info?.event?.remove?.();

      await fetchEvents?.(lastRangeRef.current.start, lastRangeRef.current.end);

      Swal.fire({
        icon: 'success',
        title: t('calendar.activityCreated'),
        timer: 900,
        showConfirmButton: false
      });
    } catch (err) {
      if (err?.message === 'cancelado') {
        info?.event?.remove?.();
        return;
      }

      const msg = err?.response?.data?.message || err.message || t('calendar.unknownError');
      console.error('❌ eventReceive error:', err);
      Swal.fire(t('calendar.errorTitle'), t('calendar.couldNotCreateActivity', { message: msg }), 'error');
      info?.event?.remove?.();
    } finally {
      suppressFetchRef.current = false;
    }
  }, [fetchEvents, isTrainerView, t]);

  /* ---------- CARGA INICIAL según rango visible ---------- */
  useEffect(() => {
    if (!window.__tzoneCalInitOnce) window.__tzoneCalInitOnce = true;
    else return;

    const api = calendarRef.current?.getApi?.();
    if (!api) return;

    const view = api.view;
    const start = view?.currentStart || new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const end = view?.currentEnd || new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59);

    lastRangeRef.current = { start, end };
    log('initial fetchEvents →', { start, end });
    fetchEvents?.(start, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- render de evento (única función) ---------- */
  const defaultEventContent = useCallback((eventInfo) => {
    const xp = eventInfo.event.extendedProps || {};
    const hasFitFile = !!xp?.completed?.fileId;

    const c = xp.completed || {};
    const p = xp.planned || {};

    const distC = c.distance ?? c.distancia ?? c.total_distance;
    const timeC = c.duration ?? c.duracion ?? c.time ?? c.total_time;

    const distP = p.distance ?? p.distancia;
    const timeP = p.durationIso || p.duration || p.duracion;

    const fmtKm = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) && n > 0 ? `${n} km` : null;
    };

    const fmtHMS = (isoOrVal) => {
      if (!isoOrVal) return null;
      const s = String(isoOrVal);
      if (s.includes(':')) return s.length === 5 ? `${s}:00` : s;
      const n = parseInt(s, 10);
      if (!Number.isFinite(n) || n <= 0) return null;
      const hh = String(Math.floor(n / 60)).padStart(2, '0');
      const mm = String(n % 60).padStart(2, '0');
      return `${hh}:${mm}:00`;
    };

    const mainMetric = fmtKm(distC) || fmtHMS(timeC) || fmtKm(distP) || fmtHMS(timeP) || '-';

    const rawType = xp?.type || eventInfo.event.title || 'otro';
    const typeKey = iconKeyFromType(rawType);
    const iconSrc = dbTypeToIcon[typeKey] || dbTypeToIcon.otro;

    return (
      <div
        className={`event-card ${hasFitFile ? 'fc-event-completed' : ''}`}
        data-event-id={eventInfo.event.id}
        title={hasFitFile ? t('calendar.completedWithFit') : t('calendar.pendingActivity')}
      >
        <div className="event-card-content">
          <img src={iconSrc} alt="icon" width="20" height="20" className="event-icon" />
          <div className="event-text">
            <div className="event-title">{safeTitle(eventInfo.event.title, rawType, t)}</div>
            <div className="event-metric"><b>{mainMetric}</b></div>
          </div>
        </div>
        {hasFitFile && <span className="event-completed-check" aria-label={t('calendar.completed')}>✔</span>}
      </div>
    );
  }, [t]);

  /* ---------- Mejora de gestos/scroll ---------- */
  useEffect(() => {
    const fc = document.querySelector('.calendar-container .fc');
    if (!fc) return;
    const noop = () => {};
    try {
      fc.addEventListener('touchmove', noop, { passive: true });
      fc.addEventListener('wheel', noop, { passive: true });
    } catch {}
    return () => {
      try {
        fc.removeEventListener('touchmove', noop);
        fc.removeEventListener('wheel', noop);
      } catch {}
    };
  }, []);

  const safeEvents = Array.isArray(events) ? events.map(normalizeEventForCalendar) : [];

  /* ---------- JSX ---------- */
  return (
    <div className="calendar-container">
      <h2>{t('calendar.title')}</h2>
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        timeZone="local"
        height="auto"
        contentHeight="auto"
        expandRows
        fixedWeekCount={false}
        dayMaxEventRows
        dayMaxEvents
        editable
        eventStartEditable
        eventDurationEditable={false}
        droppable
        dragScroll={false}
        eventLongPressDelay={300}
        selectLongPressDelay={300}
        eventDragMinDistance={3}
        handleWindowResize
        windowResizeDelay={150}
        dropAccept={(draggedEl) => {
          try {
            if (!draggedEl) return false;
            const ok =
              draggedEl.getAttribute('data-fc-item') === 'true' ||
              draggedEl.classList?.contains('fc-draggable') ||
              draggedEl.hasAttribute('data-type') ||
              draggedEl.hasAttribute('data-template');
            log('dropAccept?', ok);
            return ok;
          } catch {
            return true;
          }
        }}
        eventReceive={onEventReceive}
        eventDragStart={() => { log('eventDragStart'); suppressFetchRef.current = true; }}
        eventDragStop={() => { log('eventDragStop'); suppressFetchRef.current = false; }}
        eventDrop={async (eventInfo) => {
          log('eventDrop →', {
            id: eventInfo.event.id,
            oldStart: eventInfo.oldEvent?.start,
            newStart: eventInfo.event.start
          });

          if (
            eventInfo.oldEvent?.start &&
            eventInfo.event?.start &&
            eventInfo.oldEvent.start.toDateString() === eventInfo.event.start.toDateString()
          ) {
            log('↪️ Mismo día: no se persiste ni se refresca');
            return;
          }

          suppressFetchRef.current = true;
          try {
            const newIso = toLocalMidnightIso(eventInfo.event.start);

            let ok = true;
            let msg = '';

            if (typeof handleEventDrop === 'function') {
              const res = await handleEventDrop(eventInfo);
              if (res === false) ok = false;
              else if (res && typeof res === 'object') {
                ok = res.ok !== false;
                msg = res.message || res.msg || '';
              }
            }

            if (ok) {
              try { eventInfo.event.remove(); } catch {}
            } else {
              log('⛔ handleEventDrop indicó fallo', msg);
              eventInfo.revert();
              Swal.fire(t('calendar.errorTitle'), msg || t('calendar.couldNotMoveActivity'), 'error');
              return;
            }

            await fetchEvents?.(lastRangeRef.current.start, lastRangeRef.current.end);
            log('✅ Movimiento persistido (ISO local), refresco suave del rango visible');
            Swal.fire({
              icon: 'success',
              title: t('calendar.activityMoved'),
              timer: 900,
              showConfirmButton: false
            });
          } catch (err) {
            console.error('❌ eventDrop exception, revertiendo:', err);
            eventInfo.revert();
            Swal.fire(t('calendar.errorTitle'), t('calendar.couldNotMoveActivity'), 'error');
          } finally {
            suppressFetchRef.current = false;
          }
        }}
        longPressDelay={250}
        selectable
        eventDisplay="block"
        displayEventTime={false}
        datesSet={({ start, end }) => {
          log('datesSet →', { start, end, suppress: suppressFetchRef.current });
          if (suppressFetchRef.current) return;

          const prev = lastRangeRef.current;
          const same =
            prev.start?.getTime?.() === start.getTime() &&
            prev.end?.getTime?.() === end.getTime();
          if (same) return;

          lastRangeRef.current = { start, end };

          if (datesSetTimerRef.current) clearTimeout(datesSetTimerRef.current);
          datesSetTimerRef.current = setTimeout(() => {
            if (!suppressFetchRef.current) {
              log('datesSet → fetchEvents', { start, end });
              fetchEvents?.(start, end);
            }
          }, 200);
        }}
        select={(info) => {
          if (clipboardEvent) handlePaste(info);
          else handleDateSelect?.(info);
        }}
        events={safeEvents}
        eventClick={(eventInfo) => {
          const now = Date.now();
          const id = eventInfo.event.id;

          if (lastClickRef.current.id === id && (now - lastClickRef.current.t) < 600) {
            log('🔇 click suprimido (debounce) para id', id);
            return;
          }

          lastClickRef.current = { id, t: now };
          setSelectedEvent(eventInfo.event);
          log('eventClick → id:', id, 'extendedProps:', eventInfo.event.extendedProps);
          handleEventClick?.(eventInfo);
        }}
        eventDidMount={onEventDidMount}
        eventClassNames={(arg) =>
          arg.event.extendedProps?.completed?.fileId ? 'fc-event-completed' : ''
        }
        eventContent={customRenderEventContent || defaultEventContent}
      />

      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={!selectedEvent}
          onClick={handleCopy}
        >
          {t('calendar.copy')}
        </button>

        <button
          type="button"
          className="btn btn-outline-danger btn-sm"
          disabled={!selectedEvent}
          onClick={handleDelete}
        >
          {t('calendar.delete')}
        </button>
      </div>
    </div>
  );
};

export default AthleteCalendar;